import { getCsrfToken } from '../lib/csrf';

const API_URL = process.env.REACT_APP_API_URL || '';

if (!API_URL) {
  console.warn('REACT_APP_API_URL is not set. Betfair integration will not work.');
}

export interface BetfairStatus {
  connected: boolean;
  expired?: boolean;
  expires_at?: string;
  status?: string;
  reauth_required?: boolean;
  reason?: string;
  /** True when status could not be fetched (429/5xx) — do not treat as disconnected. */
  statusCheckFailed?: boolean;
}

export interface ConnectResponse {
  auth_url: string;
  state: string;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isTransientHttpStatus(status: number): boolean {
  return status === 429 || status === 503 || status >= 500;
}

/**
 * Check if user's Betfair account is connected.
 * Retries on rate-limit / server errors so a 429 burst does not show as disconnected.
 */
export async function checkBetfairStatus(userId: string, accessToken: string): Promise<BetfairStatus> {
  const maxAttempts = 3;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await fetch(`${API_URL}/api/betfair/status/${userId}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = (await response.json()) as BetfairStatus;
        return data;
      }

      if (isTransientHttpStatus(response.status) && attempt < maxAttempts) {
        await sleep(350 * attempt);
        continue;
      }

      if (isTransientHttpStatus(response.status)) {
        console.warn('Betfair status check rate-limited or unavailable after retries', response.status);
        return { connected: false, statusCheckFailed: true };
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    } catch (error) {
      if (attempt < maxAttempts) {
        await sleep(350 * attempt);
        continue;
      }
      console.error('Error checking Betfair status:', error);
      return { connected: false, statusCheckFailed: true };
    }
  }

  return { connected: false, statusCheckFailed: true };
}

/**
 * Initiate Betfair OAuth connection flow
 */
export async function initiateConnection(userId: string, accessToken: string): Promise<string> {
  try {
    const response = await fetch(`${API_URL}/api/betfair/connect?user_id=${userId}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to initiate connection');
    }

    const data: ConnectResponse = await response.json();
    return data.auth_url;
  } catch (error) {
    console.error('Error initiating Betfair connection:', error);
    throw error;
  }
}

export interface DisconnectResponse {
  success: boolean;
  message: string;
  betfair_revoked?: boolean;
  warning?: string;
}

export interface BetfairAccount {
  availableToBetBalance?: number;
  exposure?: number;
  retainedCommission?: number;
  exposureLimit?: number;
  discountRate?: number;
  pointsBalance?: number;
  wallet?: string;
  aiMode?: boolean;
  status?: string;
}

export interface AccountDetailsResponse {
  success: boolean;
  account: BetfairAccount;
}

/**
 * Disconnect Betfair account
 */
export async function disconnectBetfair(userId: string, accessToken: string): Promise<DisconnectResponse> {
  try {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${API_URL}/api/betfair/disconnect`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({ user_id: userId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to disconnect');
    }

    const data: DisconnectResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error disconnecting Betfair:', error);
    throw error;
  }
}

/**
 * Get Betfair account details
 */
export async function getAccountDetails(userId: string, accessToken: string, forceRefresh = false): Promise<AccountDetailsResponse> {
  try {
    const url = forceRefresh 
      ? `${API_URL}/api/betfair/account/${userId}?force=true`
      : `${API_URL}/api/betfair/account/${userId}`;
    
    const response = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch account details');
    }

    const data: AccountDetailsResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching account details:', error);
    throw error;
  }
}

export interface BetfairOddsResponse {
  success: boolean;
  marketId: string;
  marketStatus: string;
  selectionId: number;
  bestBackPrice: number | null;
  lastPriceTraded: number | null;
  availableToBack: Array<{ price: number; size: number }>;
  availableToLay: Array<{ price: number; size: number }>;
}

/**
 * Get live Betfair odds for a specific market and selection
 */
export async function getBetfairOdds(marketId: string, selectionId: number, accessToken: string): Promise<BetfairOddsResponse> {
  try {
    const response = await fetch(`${API_URL}/api/betfair/odds/${marketId}/${selectionId}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to fetch Betfair odds');
    }

    const data: BetfairOddsResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching Betfair odds:', error);
    throw error;
  }
}

export interface PlaceBetfairBetResponse {
  success: boolean;
  betId?: string;
  status: 'matched' | 'unmatched' | 'failure';
  averagePriceMatched?: number;
  sizeMatched?: number;
  errorCode?: string;
  error?: string;
  message?: string;
}

/**
 * Place a bet on Betfair
 */
export async function placeBetfairBet(
  userId: string,
  marketId: string,
  selectionId: number,
  stake: number,
  odds: number,
  accessToken: string
): Promise<PlaceBetfairBetResponse> {
  try {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${API_URL}/api/betfair/place-bet`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({
        user_id: userId,
        marketId: marketId,
        selectionId: selectionId,
        stake: stake,
        odds: odds,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to place bet on Betfair');
    }

    const data: PlaceBetfairBetResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error placing Betfair bet:', error);
    throw error;
  }
}

export interface ToggleAiModeResponse {
  success: boolean;
  message: string;
  ai_mode: boolean;
}

/**
 * Toggle AI Mode for Betfair
 */
export async function toggleAiMode(userId: string, aiMode: boolean, accessToken: string): Promise<ToggleAiModeResponse> {
  try {
    const csrfToken = await getCsrfToken();
    const response = await fetch(`${API_URL}/api/betfair/ai-mode`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'X-CSRF-Token': csrfToken,
      },
      body: JSON.stringify({
        user_id: userId,
        ai_mode: aiMode,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to toggle AI mode');
    }

    const data: ToggleAiModeResponse = await response.json();
    return data;
  } catch (error) {
    console.error('Error toggling AI mode:', error);
    throw error;
  }
}

